import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import {
  Arrangement,
  BulkDeleteCollection,
  ManagementMergeConflictsResponse,
  ManagementReader,
  ManagementResult,
  ManagementStatistic,
  Method,
} from '@thiememeulenhoff/types';
import { ManagementEndpointService } from './management.endpoint.service';

export type ReaderPricesUpdateStatus = 'running' | 'success' | 'error' | null;

@Service()
export class ManagementService {
  readonly #http = inject(HttpClient);

  readonly #endpoints = inject(ManagementEndpointService);

  fetchResultForArrangementByActor(actorId: string, arrangementCode: string) {
    const endpoint = this.#endpoints.resultFromArrangementByActor(
      actorId,
      arrangementCode,
    );
    return this.#http.get<ManagementResult>(endpoint);
  }

  fetchStatisticsByActor(actorId: string) {
    const endpoint = this.#endpoints.statisticsByActor(actorId);
    return this.#http.get<ManagementStatistic>(endpoint);
  }

  mergeActorResults(
    formerActorId: string,
    targetActorId: string,
    arrangementCode?: string,
  ) {
    return this.#http.post(
      this.#endpoints.mergeActorResults(
        formerActorId,
        targetActorId,
        arrangementCode,
      ),
      {},
    );
  }

  fetchMergeConflicts(
    formerActorId: string,
    targetActorId: string,
    arrangementCode?: string,
  ) {
    const endpoint = this.#endpoints.mergeConflicts(
      formerActorId,
      targetActorId,
      arrangementCode,
    );

    return this.#http.get<ManagementMergeConflictsResponse>(endpoint);
  }

  bulkDeleteResult(collection: BulkDeleteCollection) {
    const endpoint = this.#endpoints.bulkDeleteResult();

    return this.#http.delete(endpoint, {
      body: collection,
    });
  }

  updateReaderPrices() {
    return this.#http.post<{ status: 'running' }>(
      this.#endpoints.readerPricesUpdate(),
      {},
    );
  }

  fetchReaderPricesUpdateStatus() {
    return this.#http.get<{ status: ReaderPricesUpdateStatus }>(
      this.#endpoints.readerPricesUpdateStatus(),
    );
  }

  fetchReaders() {
    const endpoint = this.#endpoints.managementReaders();

    return this.#http.get<Array<ManagementReader>>(endpoint);
  }

  fetchManagementMethods() {
    const endpoint = this.#endpoints.managementMethods();

    return this.#http.get<Array<Method>>(endpoint);
  }

  fetchManagementArrangements() {
    const endpoint = this.#endpoints.managementArrangements();

    return this.#http.get<Array<Arrangement>>(endpoint);
  }
}
